import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
} from 'axios';

import {
  BASE_URL,
  FALLBACK_BASE_URL,
  SECOND_FALLBACK_BASE_URL,
  THIRD_FALLBACK_BASE_URL,
} from 'const';

const REQUEST_TIMEOUT_MS = 10000;

const API_BASE_URLS = [
  BASE_URL,
  FALLBACK_BASE_URL,
  SECOND_FALLBACK_BASE_URL,
  THIRD_FALLBACK_BASE_URL
];

let activeBaseUrl = BASE_URL;

type RetryableConfig = AxiosRequestConfig & {
  _baseUrlRetryCount?: number;
  _skipBaseUrlRewrite?: boolean;
};

const normalizeUrl = (url: string): string => {
  return url.replace(/\/+$/, '');
};

const isRetriableError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;

  if (status && status >= 500 && status <= 599) {
    return true;
  }

  if (error.response) {
    return false;
  }

  const message = error.message.toLowerCase();

  return (
    error.code === 'ECONNABORTED' ||
    error.code === 'ERR_NETWORK' ||
    message.includes('timeout') ||
    message.includes('network error')
  );
};

const isCanceledError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  return axios.isCancel(error) || error.code === 'ERR_CANCELED';
};

const getMatchedBaseUrl = (url: string): string | null => {
  for (const baseUrl of API_BASE_URLS) {
    const normalizedBaseUrl = normalizeUrl(baseUrl);

    if (url.startsWith(normalizedBaseUrl)) {
      return baseUrl;
    }
  }

  return null;
};

const getNextBaseUrlByRequestUrl = (url: string): string | null => {
  const matchedBaseUrl = getMatchedBaseUrl(url);

  if (!matchedBaseUrl) {
    return null;
  }

  const currentIndex = API_BASE_URLS.indexOf(matchedBaseUrl);

  if (currentIndex === -1) {
    return null;
  }

  return API_BASE_URLS[currentIndex + 1] ?? null;
};

const replaceKnownBaseUrl = (
  url: string,
  nextBaseUrl: string,
): string | null => {
  const matchedBaseUrl = getMatchedBaseUrl(url);

  if (!matchedBaseUrl) {
    return null;
  }

  return url.replace(
    normalizeUrl(matchedBaseUrl),
    normalizeUrl(nextBaseUrl),
  );
};

export const applyBaseUrlFailover = (client: AxiosInstance): void => {
  client.defaults.timeout = REQUEST_TIMEOUT_MS;

  client.interceptors.request.use((config: RetryableConfig) => {
    if (config._skipBaseUrlRewrite) {
      return config;
    }

    if (!config.url) {
      return config;
    }

    const nextUrl = replaceKnownBaseUrl(config.url, activeBaseUrl);

    if (!nextUrl) {
      return config;
    }

    return {
      ...config,
      url: nextUrl,
    };
  });

  client.interceptors.response.use(
    (response) => {
      if (response.config.url) {
        const matchedBaseUrl = getMatchedBaseUrl(response.config.url);

        if (matchedBaseUrl) {
          activeBaseUrl = matchedBaseUrl;
        }
      }

      return response;
    },
    async (error: AxiosError) => {
      if (isCanceledError(error)) {
        return Promise.reject(error);
      }

      if (!isRetriableError(error)) {
        return Promise.reject(error);
      }

      const originalConfig = error.config as RetryableConfig | undefined;

      if (!originalConfig?.url) {
        return Promise.reject(error);
      }

      const retryCount = originalConfig._baseUrlRetryCount ?? 0;

      if (retryCount >= API_BASE_URLS.length - 1) {
        return Promise.reject(error);
      }

      const nextBaseUrl = getNextBaseUrlByRequestUrl(originalConfig.url);

      if (!nextBaseUrl) {
        return Promise.reject(error);
      }

      const nextUrl = replaceKnownBaseUrl(
        originalConfig.url,
        nextBaseUrl,
      );

      if (!nextUrl) {
        return Promise.reject(error);
      }

      const retryConfig: RetryableConfig = {
        ...originalConfig,
        url: nextUrl,
        baseURL: undefined,
        timeout: originalConfig.timeout ?? REQUEST_TIMEOUT_MS,
        _baseUrlRetryCount: retryCount + 1,
        _skipBaseUrlRewrite: true,
      };

      return client.request(retryConfig);
    },
  );
};

export const buildApiUrl = (path: string): string => {
  const normalizedBaseUrl = normalizeUrl(activeBaseUrl);
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return `${normalizedBaseUrl}${normalizedPath}`;
};